// src/App.js - 支持3D预览和确认建模，包括自动识别打孔
import React, { useState, useEffect } from 'react';
import './App.css';
import Preview3D from './components/Preview3D'; // 导入预览组件

function App() {
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  
  // 新增状态：预览相关
  const [previewParams, setPreviewParams] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [waitingConfirmation, setWaitingConfirmation] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const API_URL = 'https://anahi-nonenergetic-fiona.ngrok-free.dev';

  // 初始化时获取或创建session_id
  useEffect(() => {
    let storedSessionId = localStorage.getItem('chat_session_id');
    if (!storedSessionId) {
      storedSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('chat_session_id', storedSessionId);
    }
    setSessionId(storedSessionId);
  }, []);

  // 提取几何参数（支持孔和其他几何体）
  const extractGeometryParams = (data) => {
    // 方法1: 如果AI返回结构化数据，直接使用
    if (data.geometry_params) {
      return data.geometry_params;
    }
    
    // 方法2: 如果AI返回工具调用，解析参数
    if (data.tool_calls && data.tool_calls.length > 0) {
      const toolCall = data.tool_calls[0];
      if (toolCall.name === 'create_hole') {
        // 打孔工具
        const params = toolCall.parameters || {};
        return {
          type: 'hole',
          diameter: params.diameter_mm || 4.0,
          depth: params.depth_mm || 5.0,
          x: params.x_mm || 0.0,
          y: params.y_mm || 0.0,
          surface: params.surface || 'top'
        };
      }
    }
    
    // 方法3: 从回复文本中提取（备用）
    const reply = data.reply || '';
    
    // 尝试提取孔参数（正则表达式匹配）
    const holePatterns = [
      // 模式1: "在上表面(x=10mm, y=20mm)打一个直径5mm深度8mm的孔"
      /(?:孔|打孔).*?(?:直径|孔径)\s*(\d+(?:\.\d+)?)\s*mm.*?(?:深度|深)\s*(\d+(?:\.\d+)?)\s*mm.*?(?:位置|坐标)?[^\d]*(\d+(?:\.\d+)?)\s*(?:mm|毫米)?[^\d]*(\d+(?:\.\d+)?)\s*(?:mm|毫米).*?(?:面|表面)\s*(\w+)/i,
      
      // 模式2: "在上表面打孔，直径4mm，深度5mm"
      /(?:孔|打孔).*?(?:直径|孔径)\s*(\d+(?:\.\d+)?)\s*mm.*?(?:深度|深)\s*(\d+(?:\.\d+)?)\s*mm.*?(?:面|表面)\s*(\w+)/i,
      
      // 模式3: "打孔，直径4mm，深度5mm"
      /(?:孔|打孔).*?(?:直径|孔径)\s*(\d+(?:\.\d+)?)\s*mm.*?(?:深度|深)\s*(\d+(?:\.\d+)?)\s*mm/i
    ];
    
    for (const pattern of holePatterns) {
      const match = reply.match(pattern);
      if (match) {
        // 匹配成功，返回孔参数
        return {
          type: 'hole',
          diameter: parseFloat(match[1]) || 4.0,
          depth: parseFloat(match[2]) || 5.0,
          x: parseFloat(match[3]) || 0.0,
          y: parseFloat(match[4]) || 0.0,
          surface: match[5] || 'top'
        };
      }
    }
    
    return null;
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    
    setLoading(true);
    
    // 添加用户消息到历史
    const userMessage = { 
      role: 'user', 
      content: message, 
      time: new Date() 
    };
    setChatHistory(prev => [...prev, userMessage]);
    
    try {
      const res = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: message,
          session_id: sessionId
        })
      });
      
      const data = await res.json();
      
      // 添加AI回复到历史
      const aiMessage = { 
        role: 'ai', 
        content: data.reply,
        data: data,
        time: new Date()
      };
      setChatHistory(prev => [...prev, aiMessage]);
      setResponse(data);
      
      // 重要：尝试提取几何参数（包括孔）
      const geometryParams = extractGeometryParams(data);
      
      if (geometryParams) {
        // 设置预览参数并显示预览
        setPreviewParams(geometryParams);
        setShowPreview(true);
        setWaitingConfirmation(true);
        
        // 根据参数类型显示不同的系统消息
        const surfaceMap = {
          'top': '上表面',
          'front': '前表面',
          'side': '侧面'
        };
        
        let previewMsg = '';
        if (geometryParams.type === 'hole') {
          const surface = surfaceMap[geometryParams.surface] || geometryParams.surface;
          previewMsg = `✅ 已识别孔参数: 直径${geometryParams.diameter}mm，深度${geometryParams.depth}mm，位置(${geometryParams.x},${geometryParams.y})mm，${surface}`;
        } else {
          previewMsg = `✅ 已识别几何参数。请在右侧预览区确认参数。`;
        }
        
        // 添加系统消息提示用户确认
        const systemMessage = {
          role: 'system',
          content: previewMsg,
          time: new Date()
        };
        setChatHistory(prev => [...prev, systemMessage]);
      } else {
        // 没有几何参数，检查是否有工具执行结果
        if (data.tool_result) {
          const toolResult = data.tool_result;
          if (toolResult.success) {
            const successMessage = {
              role: 'system',
              content: `✅ ${toolResult.message}`,
              time: new Date()
            };
            setChatHistory(prev => [...prev, successMessage]);
          } else {
            const errorMessage = {
              role: 'error',
              content: `❌ 工具执行失败: ${toolResult.error}`,
              time: new Date()
            };
            setChatHistory(prev => [...prev, errorMessage]);
          }
        }
      }
      
    } catch (error) {
      console.error('请求失败:', error);
      const errorMessage = { 
        role: 'error', 
        content: `❌ 请求失败: ${error.message}`,
        time: new Date()
      };
      setChatHistory(prev => [...prev, errorMessage]);
    }
    
    setLoading(false);
    setMessage('');
  };

  // 确认建模回调
  const handleConfirmModeling = async (confirmedParams) => {
    if (!sessionId || !previewParams) return;
    
    setConfirming(true);
    
    try {
      // 构建确认请求体
      const requestBody = {
        session_id: sessionId,
        geometry_params: previewParams
      };
      
      // 调用确认建模端点
      const res = await fetch(`${API_URL}/confirm-modeling`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      const data = await res.json();
      
      if (data.success) {
        // 添加确认成功消息到聊天历史
        let confirmationContent = '';
        if (previewParams.type === 'hole') {
          const surfaceMap = {
            'top': '上表面',
            'front': '前表面',
            'side': '侧面'
          };
          const surface = surfaceMap[previewParams.surface] || previewParams.surface;
          confirmationContent = `✅ 孔创建成功: 直径${previewParams.diameter}mm，深度${previewParams.depth}mm，位置(${previewParams.x},${previewParams.y})mm，${surface}`;
        } else {
          confirmationContent = `✅ ${data.message || '建模完成'}`;
        }
        
        const confirmationMessage = {
          role: 'system',
          content: confirmationContent,
          time: new Date()
        };
        setChatHistory(prev => [...prev, confirmationMessage]);
        
        // 显示工具执行结果
        if (data.tool_result) {
          const toolMessage = {
            role: 'system',
            content: `🔧 工具执行结果: ${data.tool_result.message}`,
            time: new Date()
          };
          setChatHistory(prev => [...prev, toolMessage]);
        }
        
      } else {
        // 确认失败
        const errorMessage = {
          role: 'error',
          content: `❌ 确认建模失败: ${data.error || '未知错误'}`,
          time: new Date()
        };
        setChatHistory(prev => [...prev, errorMessage]);
      }
      
    } catch (error) {
      console.error('确认建模失败:', error);
      const errorMessage = {
        role: 'error',
        content: `❌ 确认建模请求失败: ${error.message}`,
        time: new Date()
      };
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setConfirming(false);
      setWaitingConfirmation(false);
      // 不清除预览参数，保持显示已确认的模型
    }
  };

  // 修改参数回调
  const handleModifyParams = () => {
    // 添加修改提示消息
    const modifyMessage = {
      role: 'system',
      content: '✏️ 请在下方输入新的参数或调整指令。',
      time: new Date()
    };
    setChatHistory(prev => [...prev, modifyMessage]);
    
    // 重置预览状态，但保持预览显示
    setWaitingConfirmation(false);
  };

  // 关闭预览
  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewParams(null);
    setWaitingConfirmation(false);
  };

  // 重新打开预览
  const handleReopenPreview = () => {
    if (previewParams) {
      setShowPreview(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearHistory = () => {
    setChatHistory([]);
    setResponse(null);
    setPreviewParams(null);
    setShowPreview(false);
    setWaitingConfirmation(false);
    
    // 清除session_id并创建新的
    const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('chat_session_id', newSessionId);
    setSessionId(newSessionId);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // 获取预览状态文本
  const getPreviewStatus = () => {
    if (!previewParams) return '无模型';
    
    const type = previewParams.type;
    const surfaceMap = {
      'top': '上表面',
      'front': '前表面',
      'side': '侧面'
    };
    
    switch(type) {
      case 'cube':
        return `立方体 ${previewParams.size}mm`;
      case 'cuboid':
        return `长方体 ${previewParams.length}×${previewParams.width}×${previewParams.height}mm`;
      case 'cylinder':
        return `圆柱体 直径${previewParams.diameter}mm 高${previewParams.height}mm`;
      case 'hole':
        const surface = surfaceMap[previewParams.surface] || previewParams.surface;
        return `孔 Ø${previewParams.diameter}mm×${previewParams.depth}mm @ ${surface}(${previewParams.x},${previewParams.y})`;
      case 'polygon_prism':
        return `${previewParams.sides}棱柱 直径${previewParams.inscribed_diameter}mm 高${previewParams.height}mm`;
      default:
        return previewParams.label || '未知模型';
    }
  };

  return (
    <div className={`App ${showPreview ? 'with-preview' : ''}`}>
      {/* 左侧聊天区域 */}
      <div className="chat-container">
        <header className="App-header">
          <h1>🔧 SolidWorks AI 助手</h1>
          <p className="subtitle">用自然语言控制 SolidWorks 建模 • 支持3D预览确认</p>
          {showPreview && (
            <div className="preview-toggle">
              <button 
                onClick={handleClosePreview}
                className="close-preview-btn"
                title="关闭预览"
              >
                ✕ 关闭预览
              </button>
            </div>
          )}
        </header>

        <main className="App-main">
          {/* 聊天历史区域 */}
          <div className="chat-history">
            {chatHistory.length === 0 ? (
              <div className="empty-state">
                <p>💡 尝试输入：</p>
                <ul>
                  <li>"hi，介绍一下功能？"</li>
                  <li>"帮我做一个边长30mm的立方体"</li>
                  <li>"帮我做一个内切圆直径20mm的六棱柱，高40mm"</li>
                  <li>"在侧面(20,10)的位置打孔，直径4mm，深度8mm"</li>
                  <li>"在上表面中心打一个直径5mm，深度10mm的孔"</li>
                  <li>"也可以问数学，物理等常规问题"</li>
                </ul>
              </div>
            ) : (
              chatHistory.map((item, index) => (
                <div 
                  key={index} 
                  className={`chat-message ${item.role}`}
                >
                  <div className="message-header">
                    <span className="message-role">
                      {item.role === 'user' ? '👤 你' : 
                       item.role === 'ai' ? '🤖 AI助手' : 
                       item.role === 'system' ? '⚙️ 系统' : '❌ 错误'}
                    </span>
                    <span className="message-time">{formatTime(item.time)}</span>
                  </div>
                  <div className="message-content">{item.content}</div>
                  
                  {/* 显示工具执行详情 */}
                  {item.role === 'ai' && item.data?.tool_result && (
                    <div className="tool-details">
                      <small>
                        工具执行: {item.data.tool_result.success ? '✅ 成功' : '❌ 失败'}
                        {item.data.tool_result.error && ` - ${item.data.tool_result.error}`}
                      </small>
                    </div>
                  )}
                </div>
              ))
            )}
            
            {/* 等待确认的提示 */}
            {waitingConfirmation && (
              <div className="chat-message system confirmation-prompt">
                <div className="message-header">
                  <span className="message-role">⚙️ 系统提示</span>
                </div>
                <div className="message-content">
                  <p>🔄 <strong>请查看右侧预览并确认参数</strong></p>
                  <ul>
                    <li>✅ 检查3D预览是否正确</li>
                    <li>📐 确认尺寸参数</li>
                    <li>👆 点击"确认建模"开始创建</li>
                    <li>✏️ 或点击"修改参数"进行调整</li>
                  </ul>
                  <div className="confirmation-hint">
                    {confirming ? (
                      <span className="loading-text">⏳ 正在确认建模...</span>
                    ) : (
                      <span className="ready-text">🎯 模型准备就绪，等待确认</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 输入区域 */}
          <div className="input-area">
            <div className="input-wrapper">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  waitingConfirmation 
                    ? "当前正在确认参数，您可以输入新指令修改..." 
                    : "输入指令，如：在上表面(10,20)位置打孔直径5mm深度8mm"
                }
                disabled={loading || confirming}
                className="message-input"
              />
              <button 
                onClick={sendMessage} 
                disabled={loading || confirming || !message.trim()}
                className="send-button"
              >
                {loading ? '发送中...' : '发送'}
              </button>
            </div>
            
            <div className="input-hints">
              <small>按 Enter 发送，Shift+Enter 换行</small>
              {showPreview && (
                <small className="preview-hint"> | 🎨 3D预览已启用</small>
              )}
              {waitingConfirmation && (
                <small className="confirmation-hint"> | ⚠️ 等待确认建模参数</small>
              )}
            </div>
          </div>

          {/* 控制按钮 */}
          <div className="control-buttons">
            <div className="left-controls">
              <button onClick={clearHistory} className="clear-button">
                🗑️ 清空聊天
              </button>
              {!showPreview && previewParams && (
                <button 
                  onClick={handleReopenPreview}
                  className="preview-button"
                >
                  🎨 显示预览
                </button>
              )}
              {waitingConfirmation && !confirming && (
                <button 
                  onClick={() => handleConfirmModeling(previewParams)}
                  className="confirm-button"
                >
                  ✅ 确认建模
                </button>
              )}
            </div>
            
            <div className="api-status">
              <span className={`status-dot ${loading || confirming ? 'loading' : 'online'}`}></span>
              <small>后端: {loading ? '处理中' : confirming ? '确认中' : '在线'}</small>
              {previewParams && (
                <small className="model-status">
                  | 📦 {getPreviewStatus()}
                </small>
              )}
            </div>
          </div>

          {/* 调试信息（可选） */}
          {response && process.env.NODE_ENV === 'development' && (
            <details className="debug-info">
              <summary>调试信息</summary>
              <pre>{JSON.stringify(response, null, 2)}</pre>
              {previewParams && (
                <div style={{ marginTop: '1rem' }}>
                  <strong>几何参数:</strong>
                  <pre>{JSON.stringify(previewParams, null, 2)}</pre>
                </div>
              )}
            </details>
          )}
        </main>

        <footer className="App-footer">
          <p>SolidWorks AI 助手 v1.1 • 后端运行在: {API_URL}</p>
          {previewParams && (
            <p className="preview-info">
              🎯 当前模型: {getPreviewStatus()}
              {waitingConfirmation && ' • ⏳ 等待确认'}
            </p>
          )}
          <p className="session-info">
            🔑 会话ID: {sessionId ? `${sessionId.substring(0, 15)}...` : '未设置'}
          </p>
        </footer>
      </div>

      {/* 右侧预览区域 */}
      {showPreview && (
        <Preview3D
          geometryParams={previewParams}
          onConfirm={handleConfirmModeling}
          onCancel={handleModifyParams}
          isConfirming={confirming}
        />
      )}
    </div>
  );
}

export default App;